import React, { FC } from "react";

import { toast } from "react-toastify";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
} from "~/components/layouts";
import {
  MultiParentsWithMultiChildrenDropdownField,
  FilterWrapper,
  GridContainer,
} from "~/components/organisms";

import { useApisManagersRoleplayingConditionTemplatesDestroy } from "./hooks/useApisManagersRoleplayingConditionTemplatesDestroy";
import { useSearchManagersRoleplayingConditionTemplates } from "./hooks/useSearchManagersRoleplayingConditionTemplates";

import { RoleplayingConditionTemplateType } from "~/domains";

import {
  LabelWithDropDownField,
  LabelWithTextField,
  GridRow,
  OpenedMenuInGrid,
  InfiniteScrollWithMoreButton,
} from "~/ui";
import {
  Form,
  Button,
  FormSubmitButton,
  GridHeaderCells,
  BooleanDropDownField,
} from "~/ui";

export const ManagersRoleplayingConditionTemplatesIndex: FC = () => {
  const { mutate: deleteRequest } =
    useApisManagersRoleplayingConditionTemplatesDestroy();

  const {
    roleplayingConditionTemplates,
    titleKeyword,
    isLoading,
    hasNextPage,
    allBusinessSectionsDistribution,
    selectableDropdownPublicStatusIds,
    selectedDropdownPublicStatusId,
    selectableDropdownDivisions,
    selectedDropdownDivisions,
    selectableDropdownSections,
    selectedDropdownSections,
    onDivisionChange,
    onSectionChange,
    findAndRemove,
    onChangeDropdownPublicStatusId,
    fetchNextPage,
    onChangeTitleKeyword,
    onConditionReset,
    onSearchSubmit,
    setAllBusinessSectionsDistribution,
  } = useSearchManagersRoleplayingConditionTemplates();
  const breadCrumbItems = [
    {
      label: "ロールプレイングテンプレ",
      href: "/managers/roleplaying_condition_templates",
    },
    { label: "一覧", href: "/managers/roleplaying_condition_templates" },
  ];

  const handleDelete = (
    roleplayingConditionTemplate: RoleplayingConditionTemplateType,
  ) => {
    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;

    deleteRequest(
      {
        id: roleplayingConditionTemplate.id,
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          findAndRemove((item) => item.id === roleplayingConditionTemplate.id);
        },
      },
    );
  };

  const generateRoleplayingConditionTemplateLists = (
    roleplayingConditionTemplate: RoleplayingConditionTemplateType,
  ) => {
    return [
      roleplayingConditionTemplate.title,
      roleplayingConditionTemplate.allBusinessSectionsDistribution
        ? "すべての部署"
        : roleplayingConditionTemplate.businessDivisions
            .map((division) => division.name)
            .join(","),
      roleplayingConditionTemplate.allBusinessSectionsDistribution
        ? "すべての課"
        : roleplayingConditionTemplate.businessSections
            .map((section) => section.name)
            .join(","),
      roleplayingConditionTemplate.publicStatus.name,
    ];
  };

  return (
    <>
      <EmployeeLayout
        withRightSidebar={false}
        breadCrumbItems={breadCrumbItems}
      >
        <EmployeeLayoutMainContent withRightSidebar={false}>
          <FilterWrapper>
            <Form onSubmit={onSearchSubmit} className="space-y-6">
              <LabelWithTextField
                labelText="タイトル"
                type="text"
                name="title"
                value={titleKeyword}
                onChange={onChangeTitleKeyword}
                placeholder="タイトル"
              />
              <LabelWithDropDownField
                labelText="ステータス"
                name="status"
                options={selectableDropdownPublicStatusIds}
                value={selectedDropdownPublicStatusId}
                onChange={onChangeDropdownPublicStatusId}
              />
              <BooleanDropDownField
                labelText="配布先全体配布"
                name="allBusinessSectionsDistribution"
                trueLabel="すべての課に配布する"
                falseLabel="個別の課に配布する"
                onChange={setAllBusinessSectionsDistribution}
                value={allBusinessSectionsDistribution}
              />
              <MultiParentsWithMultiChildrenDropdownField
                allParents={selectableDropdownDivisions}
                parentsValue={selectedDropdownDivisions}
                parentsOnChange={onDivisionChange}
                parentRequired={false}
                parentLabel="配布先部署"
                allChildren={selectableDropdownSections}
                childrenValue={selectedDropdownSections}
                childrenOnChange={onSectionChange}
                childRequired={false}
                childLabel="配布先課"
              />
              <div className="flex space-x-4">
                <Button
                  text="リセット"
                  outline
                  onClick={onConditionReset}
                  color="gray"
                  className="w-full"
                />
                <FormSubmitButton
                  value="検索する"
                  color="primary"
                  className="w-full"
                />
              </div>
            </Form>
          </FilterWrapper>
          <InfiniteScrollWithMoreButton
            itemsLength={roleplayingConditionTemplates.length}
            nextFetchFunction={fetchNextPage}
            hasMore={hasNextPage}
            isLoading={isLoading}
            className="mt-12"
          >
            <GridContainer cols={4}>
              <GridHeaderCells
                texts={["タイトル", "配布部署", "配布課", "ステータス"]}
              />
              <>
                {roleplayingConditionTemplates.map(
                  (roleplayingConditionTemplate) => (
                    <GridRow
                      key={roleplayingConditionTemplate.id}
                      lists={generateRoleplayingConditionTemplateLists(
                        roleplayingConditionTemplate,
                      )}
                      openedMenu={
                        <OpenedMenuInGrid
                          cols={4}
                          buttons={[
                            {
                              text: "編集",
                              link: `/managers/roleplaying_condition_templates/${roleplayingConditionTemplate.id}/edit`,
                            },
                            {
                              text: "削除",
                              onClick: () =>
                                handleDelete(roleplayingConditionTemplate),
                            },
                          ]}
                        />
                      }
                    />
                  ),
                )}
              </>
            </GridContainer>
          </InfiniteScrollWithMoreButton>
        </EmployeeLayoutMainContent>
      </EmployeeLayout>
    </>
  );
};
